import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Grid,
  Link as MuiLink,
  Stack,
  Switch,
  TextField,
  Typography,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon, OpenInNew as OpenInNewIcon, Search as SearchIcon, ArrowBack as ArrowBackIcon, GitHub as GitHubIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import techStackService, { TechStackItem, TechCategory } from '../../services/techStackService';

const categories: TechCategory[] = ['Security','DevOps','Monitoring','Data','Platform','Infrastructure','Testing','Other'];

const TechStack: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<TechStackItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<Partial<TechStackItem>>({});
  const [q, setQ] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<TechCategory | 'All'>('All');
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const load = async (query?: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await techStackService.list(query);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load tech stack');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (editingId && firstFieldRef.current) firstFieldRef.current.focus(); }, [editingId]);

  const startEdit = (it: TechStackItem) => { setEditingId(it._id); setForm({ ...it }); };
  const startCreate = () => {
    setEditingId('new');
    setForm({ name: '', acronym: '', category: 'Other', description: '', benefit: '', url: '', order: (items?.length || 0) + 1, isActive: true } as Partial<TechStackItem>);
  };
  const cancelEdit = () => { setEditingId(null); setForm({}); };

  const save = async () => {
    try {
      if (editingId === 'new') {
        const created = await techStackService.create({
          name: form.name || '',
          acronym: form.acronym || undefined,
          category: (form.category as TechCategory) || 'Other',
          description: form.description || undefined,
          benefit: form.benefit || undefined,
          url: form.url || undefined,
          iconUrl: form.iconUrl || undefined,
          githubUrl: form.githubUrl || undefined,
          order: typeof form.order === 'number' ? form.order : undefined,
          isActive: typeof form.isActive === 'boolean' ? form.isActive : undefined,
        });
        setItems((prev) => [created, ...prev]);
      } else if (editingId) {
        const updated = await techStackService.update(editingId, {
          name: form.name,
          acronym: form.acronym,
          category: form.category as TechCategory,
          description: form.description,
          benefit: form.benefit,
          url: form.url,
          iconUrl: form.iconUrl,
          githubUrl: form.githubUrl,
          order: form.order,
          isActive: form.isActive,
        });
        setItems((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
      }
      cancelEdit();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Save failed');
    }
  };

  const remove = async (id: string) => {
    try {
      await techStackService.remove(id);
      setItems((prev) => prev.filter((p) => p._id !== id));
      if (editingId === id) cancelEdit();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Delete failed');
    }
  };

  const filtered = useMemo(() => {
    let list = Array.isArray(items) ? items : [];
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((i) => [i.name, i.acronym, i.category, i.description, i.benefit]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s)));
    }
    if (filterCategory !== 'All') {
      list = list.filter((i) => i.category === filterCategory);
    }
    return list;
  }, [items, q, filterCategory]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />} aria-label="Back" sx={{ mr: 2 }}>Back</Button>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search tech stack"
          aria-label="Search tech stack"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon aria-hidden="true" /></InputAdornment>) }}
          sx={{ mr: 2 }}
        />
        <TextField select size="small" label="Category" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value as any)} sx={{ width: 200, mr: 2 }}>
          <MenuItem value="All">All</MenuItem>
          {categories.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
        </TextField>
        <Button onClick={startCreate} startIcon={<AddIcon />} aria-label="Add tech stack item">Add</Button>
      </Box>

      <Typography variant="h4" component="h1" sx={{ color: '#003366', fontWeight: 700, mb: 1 }}>Tech Stack</Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>Each card explains what the tool is and the benefit it brings. Click anywhere to edit. Fully keyboard accessible.</Typography>

      {error && (<Typography role="alert" aria-live="assertive" sx={{ color: 'error.main', mb: 2 }}>{error}</Typography>)}

      {editingId === 'new' && (
        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'background.default' }}>
          <CardContent>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                <TextField inputRef={firstFieldRef} label="Name" fullWidth value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                <TextField label="Acronym" sx={{ width: 160 }} value={form.acronym || ''} onChange={(e) => setForm((f) => ({ ...f, acronym: e.target.value }))} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField select label="Category" sx={{ width: 220 }} value={(form.category as TechCategory) || 'Other'} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as TechCategory }))}>
                  {categories.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
                </TextField>
                <TextField label="Order" type="number" sx={{ width: 120 }} value={form.order ?? ''} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1 }}>
                  <Typography component="span">Active</Typography>
                  <Switch checked={!!form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} inputProps={{ 'aria-label': 'Active' }} />
                </Stack>
              </Stack>
              <TextField label="Description (what it does)" multiline minRows={2} value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              <TextField label="Benefit (why it matters)" multiline minRows={2} value={form.benefit || ''} onChange={(e) => setForm((f) => ({ ...f, benefit: e.target.value }))} />
              <TextField label="Official URL" value={form.url || ''} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} />
              <TextField label="Icon URL" value={form.iconUrl || ''} onChange={(e) => setForm((f) => ({ ...f, iconUrl: e.target.value }))} />
              <TextField label="GitHub URL" value={form.githubUrl || ''} onChange={(e) => setForm((f) => ({ ...f, githubUrl: e.target.value }))} />
              <Stack direction="row" spacing={1}>
                <Button onClick={save} startIcon={<SaveIcon />} aria-label="Save new item" variant="contained">Save</Button>
                <Button onClick={cancelEdit} startIcon={<CloseIcon />} aria-label="Cancel" color="inherit">Cancel</Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2} role="list">
        {(filtered || []).map((it) => (
          <Grid key={it._id} item xs={12} sm={6} md={4} role="listitem">
            {editingId === it._id ? (
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1}>
                      <TextField inputRef={firstFieldRef} label="Name" fullWidth value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                      <TextField label="Acronym" sx={{ width: 160 }} value={form.acronym || ''} onChange={(e) => setForm((f) => ({ ...f, acronym: e.target.value }))} />
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <TextField select label="Category" sx={{ width: 220 }} value={(form.category as TechCategory) || 'Other'} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as TechCategory }))}>
                        {categories.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
                      </TextField>
                      <TextField label="Order" type="number" sx={{ width: 120 }} value={form.order ?? ''} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} />
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 1 }}>
                        <Typography component="span">Active</Typography>
                        <Switch checked={!!form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} inputProps={{ 'aria-label': 'Active' }} />
                      </Stack>
                    </Stack>
                    <TextField label="Description (what it does)" multiline minRows={2} value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                    <TextField label="Benefit (why it matters)" multiline minRows={2} value={form.benefit || ''} onChange={(e) => setForm((f) => ({ ...f, benefit: e.target.value }))} />
                    <TextField label="Official URL" value={form.url || ''} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} />
                    <Stack direction="row" spacing={1}>
                      <Button onClick={save} startIcon={<SaveIcon />} aria-label={`Save ${it.name}`} variant="contained">Save</Button>
                      <Button onClick={cancelEdit} startIcon={<CloseIcon />} aria-label="Cancel" color="inherit">Cancel</Button>
                      <Button onClick={() => remove(it._id)} startIcon={<DeleteIcon />} aria-label={`Delete ${it.name}`} color="error">Delete</Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea
                  role="button"
                  tabIndex={0}
                  aria-label={`Edit ${it.name}`}
                  onClick={() => startEdit(it)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEdit(it); } }}
                >
                  <CardContent>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: 'space-between' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {it.iconUrl && (
                            <img
                              src={it.iconUrl}
                              alt={`${it.name} icon`}
                              style={{ width: 32, height: 32 }}
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          )}
                          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>{it.name}</Typography>
                        </Stack>
                        {it.acronym && <Chip size="small" label={it.acronym} />}
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" color="primary" label={it.category} />
                        {typeof it.order === 'number' && <Chip size="small" label={`Order ${it.order}`} />}
                        <Chip size="small" label={it.isActive ? 'Active' : 'Inactive'} color={it.isActive ? 'success' : 'default'} />
                      </Stack>
                      {it.description && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{it.description}</Typography>
                      )}
                      {it.benefit && (
                        <Typography variant="body2" sx={{ color: 'text.primary' }}><strong>Benefit:</strong> {it.benefit}</Typography>
                      )}
                      {it.url && (
                        <MuiLink href={it.url} target="_blank" rel="noopener" aria-label={`Open ${it.name} website`} sx={{ display: 'inline-flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <OpenInNewIcon fontSize="small" sx={{ mr: 0.5 }} /> Official Site
                        </MuiLink>
                      )}
                      {it.githubUrl && (
                        <MuiLink href={it.githubUrl} target="_blank" rel="noopener" aria-label={`Open ${it.name} GitHub repository`} sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }} onClick={(e) => e.stopPropagation()}>
                          <GitHubIcon fontSize="small" sx={{ mr: 0.5 }} /> GitHub
                        </MuiLink>
                      )}
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            )}
          </Grid>
        ))}
        {loading && (
          <Grid item xs={12}><Typography>Loading...</Typography></Grid>
        )}
        {!loading && filtered.length === 0 && (
          <Grid item xs={12}><Typography>No tech stack items found</Typography></Grid>
        )}
      </Grid>
    </Container>
  );
};

export default TechStack;
